import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  AvatarGroup,
  Avatar,
  Tooltip,
  IconButton,
  Button,
  Typography,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { CheckCircle, Link as LinkIcon, ExpandMore } from '@mui/icons-material';
import { WorkItem, Column } from 'api/todo/types';

const CANTRACK_CASE_URL = '/cantrack/cases';

interface TaskListViewProps {
  workItems: WorkItem[];
  columns: Column[];
  columnTotals: Record<number, number>;
  onRowClick: (task: WorkItem) => void;
  onCompleteTask: (workItemId: number, columnId: number) => void;
  onLoadMore: () => void;
}

type Order = 'asc' | 'desc';
type OrderBy = 'date_due' | 'title' | 'column_name';

export const TaskListView: React.FC<TaskListViewProps> = ({
  workItems,
  columns,
  columnTotals,
  onRowClick,
  onCompleteTask,
  onLoadMore,
}) => {
  const [showCompleted, setShowCompleted] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('date_due');
  const [displayLimit, setDisplayLimit] = useState<number>(25);

  // Determine done column (last column by sort_order)
  const doneColumnId = useMemo(() => {
    if (columns.length === 0) return null;
    const sortedColumns = [...columns].sort((a, b) => a.sort_order - b.sort_order);
    return sortedColumns[sortedColumns.length - 1]?.column_id || null;
  }, [columns]);

  // Filter work items based on showCompleted toggle
  const filteredWorkItems = useMemo(() => {
    if (showCompleted || !doneColumnId) {
      return workItems;
    }
    return workItems.filter((item) => item.column_id !== doneColumnId);
  }, [workItems, showCompleted, doneColumnId]);

  // Comparator function for sorting
  const getComparator = (order: Order, orderBy: OrderBy) => {
    return (a: WorkItem, b: WorkItem) => {
      let aValue: any;
      let bValue: any;

      switch (orderBy) {
        case 'date_due':
          // Null dates sort to the end regardless of sort direction
          if (!a.date_due && !b.date_due) return 0;
          if (!a.date_due) return 1;
          if (!b.date_due) return -1;
          aValue = new Date(a.date_due).getTime();
          bValue = new Date(b.date_due).getTime();
          break;
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'column_name':
          aValue = (a.column_name || '').toLowerCase();
          bValue = (b.column_name || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (bValue < aValue) {
        return order === 'desc' ? -1 : 1;
      }
      if (bValue > aValue) {
        return order === 'desc' ? 1 : -1;
      }
      return 0;
    };
  };

  // Stable sort function
  const stableSort = <T,>(array: T[], comparator: (a: T, b: T) => number): T[] => {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Sort the filtered work items
  const sortedWorkItems = useMemo(() => {
    return stableSort(filteredWorkItems, getComparator(order, orderBy));
  }, [filteredWorkItems, order, orderBy]);

  // Display pagination
  const totalServerCount = useMemo(() =>
    Object.values(columnTotals).reduce((sum, count) => sum + count, 0),
  [columnTotals]);

  const displayedWorkItems = useMemo(() =>
    sortedWorkItems.slice(0, displayLimit),
  [sortedWorkItems, displayLimit]);

  const hasMoreOnServer = workItems.length < totalServerCount;
  const hasMore = displayLimit < sortedWorkItems.length || hasMoreOnServer;
  const remainingCount = Math.max(totalServerCount - Math.min(displayLimit, sortedWorkItems.length), 0);

  const handleLoadMore = () => {
    const newLimit = displayLimit + 25;
    setDisplayLimit(newLimit);
    if (newLimit >= workItems.length && hasMoreOnServer) {
      onLoadMore();
    }
  };

  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setDisplayLimit(25);
  };

  // Helper function to look up column name by column_id
  const getColumnName = (columnId: number): string => {
    const column = columns.find((c) => c.column_id === columnId);
    return column?.name || 'Unknown';
  };

  const handleRowClick = (task: WorkItem, event: React.MouseEvent) => {
    // Don't trigger row click if clicking on action buttons
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('[role="button"]')) {
      return;
    }
    onRowClick(task);
  };

  const handleCompleteTask = (event: React.MouseEvent, task: WorkItem) => {
    event.stopPropagation();
    if (doneColumnId) {
      onCompleteTask(task.work_item_id, doneColumnId);
    }
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 } }}>
      <Paper elevation={3}>
        {/* Header with toggle */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 0 },
            p: { xs: 1.5, sm: 2 },
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Tasks</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showCompleted}
                onChange={(e) => { setShowCompleted(e.target.checked); setDisplayLimit(25); }}
                size="small"
              />
            }
            label="Show completed"
            sx={{ 
              '& .MuiFormControlLabel-label': { 
                fontSize: { xs: '0.875rem', sm: '1rem' } 
              } 
            }}
          />
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: { xs: 150, sm: 200 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  <TableSortLabel
                    active={orderBy === 'title'}
                    direction={orderBy === 'title' ? order : 'asc'}
                    onClick={() => handleRequestSort('title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 100, sm: 120 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  <TableSortLabel
                    active={orderBy === 'column_name'}
                    direction={orderBy === 'column_name' ? order : 'asc'}
                    onClick={() => handleRequestSort('column_name')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left" sx={{ minWidth: { xs: 100, sm: 120 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Assignees
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 100, sm: 130 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                  <TableSortLabel
                    active={orderBy === 'date_due'}
                    direction={orderBy === 'date_due' ? order : 'asc'}
                    onClick={() => handleRequestSort('date_due')}
                  >
                    Due Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 100, sm: 130 }, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                  Case
                </TableCell>
                <TableCell align="right" sx={{ minWidth: { xs: 100, sm: 120 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedWorkItems.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {showCompleted ? 'No tasks found' : 'No active tasks found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedWorkItems.map((task) => {
                  const isDone = doneColumnId !== null && task.column_id === doneColumnId;
                  return (
                    <TableRow
                      key={task.work_item_id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={(e) => handleRowClick(task, e)}
                    >
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: 'inherit' }}>
                            {task.title}
                          </Typography>
                          {task.primary_work_item_id != null && (
                            <Tooltip title="Linked from another board">
                              <LinkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        <Chip
                          label={getColumnName(task.column_id)}
                          size="small"
                          color={isDone ? 'success' : 'default'}
                          sx={{ fontSize: 'inherit' }}
                        />
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                          {task.assignees && task.assignees.length > 0 ? (
                            <AvatarGroup 
                              max={4} 
                              sx={{ 
                                '& .MuiAvatar-root': { 
                                  width: { xs: 24, sm: 32 }, 
                                  height: { xs: 24, sm: 32 }, 
                                  fontSize: { xs: '0.625rem', sm: '0.875rem' } 
                                } 
                              }}
                            >
                              {task.assignees.map((assignee) => (
                                <Tooltip key={assignee.user_id} title={assignee.name}>
                                  <Avatar>{assignee.name.charAt(0).toUpperCase()}</Avatar>
                                </Tooltip>
                              ))}
                            </AvatarGroup>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 'inherit' }}>
                              —
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                        {task.date_due ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: 'inherit',
                              color: new Date(task.date_due) < new Date() && !isDone ? 'error.main' : 'text.secondary',
                            }}
                          >
                            {new Date(task.date_due).toLocaleDateString()}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 'inherit' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>
                        {task.case_title && task.case_id ? (
                          <Chip
                            icon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
                            label={task.case_title.length > 20 ? `${task.case_title.substring(0, 30)}...` : task.case_title}
                            size="small"
                            variant="outlined"
                            clickable
                            onClick={(e) => {
                              e.stopPropagation();
                              window.location.href = `${CANTRACK_CASE_URL}/${task.case_id}`;
                            }}
                            sx={{ fontSize: 'inherit', maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 'inherit' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {!isDone && doneColumnId && (
                          <Tooltip title="Mark as complete (move to last column)">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={(e) => handleCompleteTask(e, task)}
                              sx={{
                                border: '1px solid',
                                borderColor: 'success.main',
                                '&:hover': {
                                  backgroundColor: 'success.main',
                                  '& .MuiSvgIcon-root': {
                                    color: 'white',
                                  },
                                },
                              }}
                            >
                              <CheckCircle sx={{ fontSize: { xs: 20, sm: 24 } }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {hasMore && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 1 }}>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<ExpandMore />}
                      onClick={handleLoadMore}
                      sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                      Load more ({remainingCount} remaining)
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
